import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext } from 'react';
import { useForm } from 'react-hook-form';

import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import FieldError from '@/forms/FieldError';
import useDischargeReasons from '@/hooks/useDischargeReasons';
import EpisodeOfCareRepository from '@/repositories/EpisodeOfCareRepository/EpisodeOfCareRepository';
import Button from '@/ui/components/Button';
import InputGroup from '@/ui/components/Form/InputGroup';
import Label from '@/ui/components/Form/Label';
import Spinner from '@/ui/components/Spinner';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

import { EditDischargeReasonsFormPayload, EditDischargeReasonsFormValues, Props } from './types';

const EditDischargeReasonsForm = ({
  dischargeReasons: initialDischargeReasons,
  episodeOfCareId,
  onSuccess,
  personId
}: Props) => {
  const { addMessage } = useContext(AppContext);
  const queryClient = useQueryClient();
  const episodeOfCareRepository = EpisodeOfCareRepository();
  const { data: dischargeReasons, isPending: isLoadingDischargeReasons } = useDischargeReasons();

  const initialValues: EditDischargeReasonsFormValues = {
    discharge_reasons: initialDischargeReasons
  };

  const {
    formState: { errors },
    handleSubmit,
    register
  } = useForm({ defaultValues: initialValues });

  const update = useMutation({
    mutationFn: ({ values }: { values: EditDischargeReasonsFormPayload }) => {
      return episodeOfCareRepository.updateDischargeReasons({
        params: values,
        episodeOfCareId
      });
    },
    onSuccess: () => {
      addMessage({ level: 'success', text: 'Successfully updated discharge reasons' });
      void queryClient.invalidateQueries({ queryKey: ['getPerson', personId] });
      onSuccess && onSuccess();
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const submitForm = (values: EditDischargeReasonsFormValues) => {
    const payload = {
      discharge_reasons: values.discharge_reasons
        .map((dischargeReasonString) => {
          return dischargeReasons?.find(
            (dischargeReasonOption) => dischargeReasonOption.name === dischargeReasonString
          )?.id;
        })
        .filter((dischargeReason): dischargeReason is string => dischargeReason !== undefined)
    };
    update.mutate({ values: payload });
  };

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      <InputGroup>
        {isLoadingDischargeReasons ? (
          <Spinner label="Loading discharge reasons" />
        ) : dischargeReasons ? (
          <>
            <Label htmlFor="" text="Discharge Reason">
              {Object.values(dischargeReasons).map((dischargeReason) => {
                const id = `discharge-reason-${dischargeReason.id}`;

                return (
                  <div key={id}>
                    <Label htmlFor={id} reverse text={dischargeReason.name}>
                      <input
                        {...register('discharge_reasons', {
                          required: 'At least one discharge reason is required'
                        })}
                        className="mr-1 mb-1"
                        id={id}
                        type="checkbox"
                        value={dischargeReason.name}
                      />
                    </Label>
                  </div>
                );
              })}
            </Label>
          </>
        ) : null}

        <FieldError message={errors.discharge_reasons?.message} />
      </InputGroup>

      <InputGroup>
        <Button disabled={update.isPending} type="submit">
          {update.isPending ? <Spinner color={Color.WHITE} label="Submitting..." /> : 'Submit'}
        </Button>
      </InputGroup>
    </form>
  );
};

export default EditDischargeReasonsForm;
